import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Slide,
  Grid,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import {
  exportCoupons,
  generateCoupons,
  updateGeneratedCoupons,
  previewCouponss,
} from "../../../Services/authService";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import { StyledTextField } from "../../../Components/Common/StyledTextField";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import * as XLSX from "xlsx";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "./Coupons.css";
import { DropDownIcon } from "../../../Components/Layouts/CustomIcons";
import LoadingAnimation from "../../../Components/Common/LoadingAnimation";
import LocationPopup from "../../Views/LocationPopup";
import { CalendarToday } from "@mui/icons-material";

// Slide Transition Function
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

function CouponsModal({ open, onClose, selectedCoupon, fetchCoupons }) {
  const { t, i18n } = useTranslation();
  const { mode } = useThemeContext();

  const btnColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const modalBackgrounColor =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const inputBoxColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const subTextColor =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;
  const creditArcColor =
    mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
  const shipArcColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const dropCircle =
    mode === "dark" ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
  const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;

  const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  const [totalCoupons, setTotalCoupons] = useState("");
  const [usedCoupons, setUsedCoupons] = useState(0);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedALLLocations, setSelectedAllLocations] = useState(false);
  const [selectedLocationsDetails, setSelectedLocationsDetails] = useState([]);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewCoupons, setPreviewCoupons] = useState([]);

  // Colors for each section of the pie chart
  const COLORS = [creditArcColor, shipArcColor];

  const [formData, setFormData] = useState({
    // id: 0,
    totalCoupons: "",
    name: "",
    couponPrice: "",
    startDate: "",
    endDate: "",
    vendingMachineIds: [],
  });

  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  // Capture edit vs. create at save time: the parent nulls `selectedCoupon` on
  // close, so the notification can't rely on it at render time.
  const [isEditSuccess, setIsEditSuccess] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    setTotalCoupons("");
    setSelectedLocations([]);
    setSelectedLocationsDetails([]);
    if (selectedCoupon) {
      setFormData(selectedCoupon);
      setSelectedLocations(selectedCoupon.vendingMachineDetails);
      const combinedLocations = selectedCoupon?.vendingMachineDetails.map(
        (machineId, index) => ({
          machineId,
          ...selectedCoupon.vendingMachineDetails[index], // Assuming vendingMachineDetails is an array with corresponding details
        }),
      );
      setSelectedLocationsDetails(combinedLocations);
      setTotalCoupons(selectedCoupon?.totalCoupons);
      setUsedCoupons(selectedCoupon?.usageCount);
      const allSelectedMachines = selectedCoupon?.vendingMachineDetails
        ? selectedCoupon?.vendingMachineDetails.map((location) => location.id)
        : [];
      // Update formData with vendingMachineIds while preserving other fields
      setFormData((prevFormData) => ({
        ...prevFormData, // Preserve existing form data fields
        vendingMachineIds: allSelectedMachines, // Add or update vendingMachineIds
      }));
      setSelectedLocations(allSelectedMachines);
    } else {
      setFormData({
        // id: 0,
        totalCoupons: "",
        name: "",
        couponPrice: "",
        startDate: "",
        endDate: "",
        vendingMachineIds: [],
      });
    }
  }, [selectedCoupon]);

  const pieChartData = [
    {
      name: "Remaining Coupons",
      value: (usedCoupons / totalCoupons) * 100,
      originalValue: usedCoupons,
    },
    {
      name: "Used Coupons",
      value: 100 - (usedCoupons / totalCoupons) * 100,
      originalValue: totalCoupons,
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (name === "totalCoupons") {
      setTotalCoupons(value ? parseInt(value, 10) : "");
      setUsedCoupons(0);
      // Live 10k cap: show the error while typing, clear it once corrected
      if (value && Number(value) > 10000) {
        setErrors((prev) => ({
          ...prev,
          totalCoupons: t("coupons.maxCouponsLimit"),
        }));
        setGeneralError(t("coupons.maxCouponsLimit"));
      } else if (errors.totalCoupons) {
        setErrors((prev) => ({ ...prev, totalCoupons: "" }));
        setGeneralError("");
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = true;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t("coupons.nameMinLength");
    }
    if (!formData.totalCoupons)
      newErrors.totalCoupons = t("coupons.NoOfCoupons");
    else if (Number(formData.totalCoupons) > 10000)
      newErrors.totalCoupons = t("coupons.maxCouponsLimit");
    if (!formData.couponPrice)
      newErrors.couponPrice = t("coupons.CouponPricerequired");
    if (!formData.startDate)
      newErrors.startDate = t("discounts.Startdaterequired");
    if (!formData.endDate) newErrors.endDate = t("discounts.Enddaterequired");

    if (formData.vendingMachineIds.length === 0) {
      newErrors.vendingMachines = t("discounts.LOCATION_REQUIRED");
    }
    setErrors(newErrors);
    // Show one message only: a specific field error (coupon count over the
    // 10k limit, or name too short) wins over the generic fallback
    setGeneralError(
      Object.keys(newErrors).length === 0
        ? ""
        : typeof newErrors.totalCoupons === "string"
          ? newErrors.totalCoupons
          : typeof newErrors.name === "string"
            ? newErrors.name
            : t("membershipCards.Pleasefill"),
    );
    return Object.keys(newErrors)?.length === 0;
  };

  // Re-run validation on language switch so visible messages re-translate
  // (they are stored in state as already-translated strings)
  useEffect(() => {
    if (Object.keys(errors).length > 0) validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      if (formData.id) {
        await updateGeneratedCoupons(formData.id, formData);
        setIsEditSuccess(true);
      } else {
        await generateCoupons(formData);
        setIsEditSuccess(false);
      }

      // Reset the form
      setFormData({
        // id: 0,
        totalCoupons: "",
        name: "",
        couponPrice: "",
        startDate: "",
        endDate: "",
        vendingMachineIds: [],
      });

      setSelectedLocations([]); // Clear selected locations
      setSuccessNotification(true); // Show success notification
      onClose(); // Close the dialog
      fetchCoupons(); // Refresh the table data
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const fieldErrors = error?.response?.data?.fieldErrors;

      if (detail?.includes("already exists")) {
        setGeneralError(t("coupons.couponNameAlreadyExists"));
      } else if (
        fieldErrors?.some(
          (err) =>
            err.field === "couponPrice" &&
            err.message === "Coupon price must be greater than zero."
        )
      ) {
        setGeneralError(t("coupons.couponValueMustBeGreaterThanZero"));
      } else if (
        fieldErrors?.some(
          (err) =>
            err.field === "totalCoupons" &&
            err.message === "Coupon Number must be greater than 0."
        )
      ) {
        setGeneralError(t("coupons.totalNoOfCoupons"));
      } else {
        setGeneralError(t("coupons.totalNoOfCouponsError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSubmit = (locations, isAllSelected) => {
    setSelectedAllLocations(isAllSelected);
    // Variables to store selected machines and machine details separately
    const selectedMachineIds = [];
    const selectedMachinesDetails = [];

    // Loop through locations to gather selected machine data
    locations.forEach((location) => {
      // For each machine in the location, store its full details
      location.machines.forEach((machine) => {
        // Push the machine details with location info into selectedMachinesDetails
        selectedMachinesDetails.push({
          machineId: machine.machineId,
          street: machine.street,
          city: location.location,
          location: machine.location, // Add location field as well
        });

        // Add machine ID to selectedMachineIds
        selectedMachineIds.push(machine.machineId);
      });
    });

    // Update the formData with selected machine IDs
    setSelectedLocationsDetails(selectedMachinesDetails); // Store full details
    setFormData({
      ...formData,
      vendingMachineIds: selectedMachineIds, // Update formData with only machine IDs
    });
  };

  const handleExport = async (exportValue, id) => {
    const isUsed = exportValue === "usageReport" ? true : false;

    if (exportValue === "preview") {
      handlePreview(id);
      setStatusFilter(""); // ✅ reset dropdown
      return;
    }

    try {
      setLoading(true);
      const fileBlob = await exportCoupons(selectedCoupon?.id, isUsed);

      if (fileBlob) {
        // The backend bakes English headers/status into the sheet, so
        // translate it client-side, same idea as the list-page export
        // (Coupons.js handleExport). Unmapped values pass through unchanged.
        const headerLabels = {
          "Name": t("coupons.name"),
          "Coupon Code": t("coupons.couponCodes"),
          "Coupon Codes": t("coupons.couponCodes"),
          "Coupon Price": t("coupons.couponPrice"),
          "Start Date": t("coupons.startDate"),
          "End Date": t("coupons.endDate"),
          "Location": t("coupons.location"),
          "Machine Number": t("coupons.machineNumber"),
          "Usage Count": t("coupons.usageCount"),
          "Usage Date": t("coupons.usageDate"),
          "Is Used": t("coupons.used"),
          "Total Coupons": t("coupons.totalCoupons"),
          "Date": t("membershipCards.Date"),
          "Status": t("coupons.status"),
          "Used": t("coupons.used"),
        };
        const statusLabels = {
          Active: t("membershipCards.active"),
          Inactive: t("membershipCards.inactive"),
          Expired: t("membershipCards.expired"),
          Used: t("coupons.used"),
          Yes: t("chatbot.bulkProduct_yes"),
          No: t("chatbot.bulkProduct_no"),
        };

        const workbook = XLSX.read(await fileBlob.arrayBuffer(), {
          type: "array",
        });
        workbook.SheetNames.forEach((name) => {
          const rows = XLSX.utils.sheet_to_json(workbook.Sheets[name], {
            header: 1,
          });
          if (!rows.length) return;
          // Translate status values only in status-like columns, so coupon
          // names that happen to read "Active" are left alone
          const statusCols = rows[0]
            .map((h, i) => (/status|used/i.test(h) ? i : -1))
            .filter((i) => i !== -1);
          rows[0] = rows[0].map((h) => headerLabels[h] || h);
          for (let r = 1; r < rows.length; r++) {
            statusCols.forEach((c) => {
              rows[r][c] = statusLabels[rows[r][c]] || rows[r][c];
            });
          }
          workbook.Sheets[name] = XLSX.utils.aoa_to_sheet(rows);
        });

        const timestamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);
        XLSX.writeFile(workbook, `coupons_${selectedCoupon?.id}_${timestamp}.xlsx`);
        setStatusFilter("");
      } else {
        console.error(t("coupons.noDataReceived"));
      }
    } catch (error) {
      console.error(
        t("coupons.errorDownloadingFile"),
        error.response?.data || error.message,
      );
    } finally {
      setLoading(false);
    }
  };


  const today = new Date().toISOString().split("T")[0];

  const handlePreview = async (couponId) => {
    try {
      const res = await previewCouponss(couponId);
      setPreviewCoupons(res); // ✅ use res directly
      setOpenPreview(true);
    } catch (error) {
      console.error("Error loading coupon preview", error);
    }
  };

  const resetForm = () => {
    setFormData({
      totalCoupons: "",
      name: "",
      couponPrice: "",
      startDate: "",
      endDate: "",
      vendingMachineIds: [],
    });

    setErrors({});
    setGeneralError("");

    setSelectedLocations([]);
    setSelectedLocationsDetails([]);
    setSelectedAllLocations(false);

    setTotalCoupons("");
    setUsedCoupons(0);
  };

  const handleClose = () => {
    resetForm();
    setStatusFilter("");
    onClose();
  };
  return (
    <>
      {loading && <LoadingAnimation />}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            position: "absolute",
            right: 10,
            margin: 0,
            height: "auto", // Full height
            borderRadius: 3,
            background: modalBackgrounColor,
            width: "97%",
            "@media (max-width: 427px)": {
              right: 5,
            },
          },
        }}
      >
        {/* Title with Close Icon */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ",
            },
          }}
        >
          <span className="franchise-heading-text">
            {selectedCoupon
              ? t("coupons.editCoupons")
              : t("coupons.nameofCouponOffer")}
          </span>
          <Box>
            {/* Export Btn */}
            {selectedCoupon ? (
              <Select
                value={statusFilter}
                onChange={(e) => {
                  const exportValue = e.target.value; // Get the id or other parameter for the export
                  setStatusFilter(exportValue); // Set filter state if needed
                  handleExport(exportValue, selectedCoupon.id); // Call the export function
                }}
                displayEmpty
                size="small"
                IconComponent={(props) => (
                  <DropDownIcon color1={dropCircle} color2="#000" {...props} />
                )} // Use the custom DropDownIcon component
                sx={{
                  backgroundColor: LIGHTCOLORS.Secondary500,
                  color: "#000",
                  borderRadius: "24px",
                  border: "none",
                  textAlign: "center",
                  paddingRight: "12px",
                  "& .MuiSelect-select": {
                    padding: "9px 20px 9px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: "16px",
                      border: "none",
                      backgroundColor: modalBackgrounColor,
                      padding: "10px",
                      marginTop: "10px",
                    },
                  },
                }}
                renderValue={(selected) => {
                  if (selected === "") {
                    return (
                      <p className="sort-filter-text">
                        {t("membershipCards.export")}
                      </p>
                    ); // Show "Sort" as placeholder when no value selected
                  }
                  return selected;
                }}
              >
                <MenuItem className="menu-item-sec" value="couponCodes">
                  {t("coupons.couponCodes")}
                </MenuItem>
                <MenuItem className="menu-item-sec" value="usageReport">
                  {t("coupons.usageReport")}
                </MenuItem>
                <MenuItem className="menu-item-sec" value="preview">
                  {t("coupons.preview")}
                </MenuItem>
              </Select>
            ) : (
              ""
            )}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            overflowY: "auto",
            paddingTop: 2,
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ",
            },
            marginRight: 1,
            '&::-webkit-scrollbar': {
              width: '8px !important',
              height: '8px !important',
              padding: '2px !important',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: scroll1,
              borderRadius: '10px',
              border: `1px solid ${scroll}`,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: scroll,
              borderRadius: '10px',
              width: '3px !important',
              border: '3px solid transparent',
              backgroundClip: 'padding-box',
              '&:hover': {
                backgroundColor: scroll,
              },
            },
            // const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;

            // const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

          }}
        >
          {" "}
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            {/* Name Fields */}
            <Box>
              <StyledTextField
                label={`${t("coupons.nameofCoupon")}`}
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                inputBoxColor={inputBoxColor}
                required
              />

            </Box>

            {/* Total Coupons Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <StyledTextField
                numberType="number"
                onWheel={(e) => {
                  e.preventDefault();
                }}
                label={`${t("coupons.noofCoupons")}`}
                name="totalCoupons"
                value={formData.totalCoupons}
                onChange={handleChange}
                error={errors.totalCoupons}
                inputBoxColor={inputBoxColor}
                required
                inputProps={{
                  min: 1, // Prevents negative numbers and zero
                  step: 1, // Allows only whole numbers
                }}
              />

            </Box>

            {/* Responsive Pie Chart */}
            {totalCoupons > 0 && (
              <>
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: 150, sm: 190, md: 185, lg: 195, xl: 210 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        startAngle={180}
                        endAngle={0}
                        cx="50%"
                        cy="90%"
                        innerRadius="140%"
                        outerRadius="155%"
                        cornerRadius={15}
                        paddingAngle={2}
                        dataKey="value"
                        stroke="none"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                {/* Sales Breakdown */}
                <Grid
                  container
                  justifyContent="space-around"
                  sx={{
                    width: "100%",
                    alignItems: "center",
                    marginTop: "-1.5rem",
                  }}
                >
                  <Box textAlign="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 1.5,
                      }}
                    >
                      <h3 className="sections-headings">
                        {pieChartData[0].originalValue}
                      </h3>
                    </Box>
                    <p style={{ color: subTextColor }} className="label-text">
                      {t("coupons.used")}
                    </p>
                  </Box>

                  <Box textAlign="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 1.5,
                      }}
                    >
                      <h3 className="sections-headings">
                        {pieChartData[1].originalValue}
                      </h3>
                    </Box>
                    <p style={{ color: subTextColor }} className="label-text">
                      {t("coupons.total")}
                    </p>
                  </Box>
                </Grid>
              </>
            )}

            {/* Coupon Price */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <StyledTextField
                label={` ${t("coupons.PriceEachCoupon")}`}
                name="couponPrice"
                numberType="number"
                value={formData.couponPrice}
                onChange={handleChange}
                error={errors.couponPrice}
                inputBoxColor={inputBoxColor}
                required
              />
            </Box>

            {/* Start Date */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                display: "flex",
                alignItems: "center",
                backgroundColor: inputBoxColor,
                borderRadius: "8px",
              }}
            >
              <StyledTextField
                label={t("coupons.startDate")}
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                error={errors.startDate}
                required
                isDate
                inputBoxColor={inputBoxColor}
                inputProps={{ min: today }}
              />
              <CalendarToday
                sx={{
                  fontSize: "14px",
                  position: "absolute",
                  top: 29,
                  right: 15,
                  opacity: mode === "dark" ? 100 : 0, // Change color based on theme
                  color: mode === "dark" ? "#fff" : "#000", // Change color based on theme
                  pointerEvents: "none",
                }}
              />
              {/* </Box> */}
            </Box>

            {/* End Date */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                display: "flex",
                alignItems: "center",
                backgroundColor: inputBoxColor,
                borderRadius: "8px",
              }}
            >
              <StyledTextField
                label={t("coupons.endDate")}
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                error={errors.endDate}
                required
                isDate
                inputBoxColor={inputBoxColor}
                inputProps={{ min: formData.startDate || today }}
              />
              <CalendarToday
                sx={{
                  fontSize: "14px",
                  position: "absolute",
                  top: 29,
                  right: 15,
                  opacity: mode === "dark" ? 100 : 0, // Change color based on theme
                  color: mode === "dark" ? "#fff" : "#000", // Change color based on theme
                  pointerEvents: "none",
                }}
              />
            </Box>

            {/* Select Locations */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px 16px",
                borderRadius: "8px",
                width: "100%",
                backgroundColor: inputBoxColor,
                cursor: "pointer",
              }}
              onClick={() => setLocationModalOpen(true)}
            >
              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {/* Label */}
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: errors.vendingMachines ? "#d32f2f" : subTextColor,
                    mb: 0.5,
                  }}
                >
                  {t("coupons.selectLocations")}
                </Typography>

                {/* Selected Locations */}
                {selectedLocationsDetails.length > 0 && (
                  <Typography
                    sx={{
                      color: "text.primary",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {selectedALLLocations
                      ? t("coupons.allMachinesSelected")
                      : selectedLocationsDetails
                        .map(
                          (location) =>
                            `${location.location} - ${location.street}`,
                        )
                        .join(", ")}
                  </Typography>
                )}
              </Box>

              <ArrowDropDownIcon sx={{ color: subTextColor }} />
            </Box>
            <LocationPopup
              open={locationModalOpen}
              onClose={() => setLocationModalOpen(false)}
              selectedMachineLocations={selectedLocations}
              onSubmit={handleLocationSubmit}
              excludeMachineType="Return Vending Machine"
            />

            {generalError && (
              <Typography
                color="error"
                variant="body2"
                sx={{ textAlign: "center", marginTop: 1 }}
              >
                {generalError}
              </Typography>
            )}
            {/* Action Buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <button
                type="button"
                style={{ color: btnColor, borderColor: btnColor }}
                className="cancel-btn"
                onClick={handleClose}
              >
                {t("membershipCards.cancel")}
              </button>
              <button
                type="button"
                style={{ background: btnColor }}
                className="save-btn"
                onClick={handleSave}
              >
                {t("membershipCards.save")}
              </button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          {t("coupons.couponCodesPreview")}
          <IconButton onClick={() => setOpenPreview(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {previewCoupons.length === 0 ? (
            <Typography>No coupons found</Typography>
          ) : (
            <Grid container spacing={1}>
              {previewCoupons.map((code, index) => (
                <Grid item xs={6} key={index}>
                  <Box
                    sx={{
                      border: "1px dashed #ccc",
                      borderRadius: "6px",
                      padding: "8px",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {code}
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
      {/* Use Notification Component */}
      <SuccessNotification
        open={successNotification}
        message={t(
          isEditSuccess
            ? "coupons.updateSuccess"
            : "coupons.generateSuccess",
        )}
        severity="success"
        onClose={() => setSuccessNotification(false)}
      />
    </>
  );
}

export default CouponsModal;
